import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import {
  Badge,
  Button,
  Card,
  CardBody,
  Col,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Input,
  Label,
  Row,
  Spinner,
  UncontrolledDropdown,
} from "reactstrap";
import { TableSpinner } from "../../components";
import { NoResultFound } from "../../components/Common";
import { QuestionDto, UpdateQuestionRequest } from "../../interfaces";
import { LocalizedProperty } from "../../interfaces/common";
import {
  useGetQuestionsByLessonQuery,
  useDeleteQuestionMutation,
  useToggleQuestionStateMutation,
  useUpdateQuestionMutation,
  useUpdateQuestionCorrectOptionMutation,
} from "../../store/api/questionApi";
import { QuestionModelEnum, QuestionOptionEnum, RoleEnum } from "../../enums";
import DeleteModal from "../../components/Common/DeleteModal";
import { AddEditQuestionsModal } from "./AddEditQuestionModal";
import { AccountState } from "../../store/slices/accountSlice";

type TProps = {
  subjectId: string;
  lessonId: string;
  model: QuestionModelEnum;
};

const QUESTION_OPTIONS: QuestionOptionEnum[] = [
  QuestionOptionEnum.OPTION_A,
  QuestionOptionEnum.OPTION_B,
  QuestionOptionEnum.OPTION_C,
  QuestionOptionEnum.OPTION_D,
];

const OPTION_FIELD_MAP: Record<
  QuestionOptionEnum,
  "optionA" | "optionB" | "optionC" | "optionD"
> = {
  [QuestionOptionEnum.OPTION_A]: "optionA",
  [QuestionOptionEnum.OPTION_B]: "optionB",
  [QuestionOptionEnum.OPTION_C]: "optionC",
  [QuestionOptionEnum.OPTION_D]: "optionD",
};

const OPTION_LETTER_MAP: Record<QuestionOptionEnum, string> = {
  [QuestionOptionEnum.OPTION_A]: "A",
  [QuestionOptionEnum.OPTION_B]: "B",
  [QuestionOptionEnum.OPTION_C]: "C",
  [QuestionOptionEnum.OPTION_D]: "D",
};

const stripHtml = (html: string): string => {
  if (!html) return "";
  const doc = new DOMParser().parseFromString(html, "text/html");
  return (doc.body.textContent || "").replace(/\s+/g, " ").trim();
};

const getQuestionTextHtml = (item: QuestionDto) =>
  item.contents.find((c) => c.type === "text")?.text || "";

const questionHasImage = (item: QuestionDto) =>
  item.contents.some((c) => c.type === "image" && c.image);

const getOptionLocalized = (
  item: QuestionDto,
  option: QuestionOptionEnum,
): LocalizedProperty | undefined => item[OPTION_FIELD_MAP[option]];

const getOptionDisplayText = (
  option: LocalizedProperty | undefined,
  language: string,
): string => {
  if (!option) return "";
  const raw =
    language === "ar" ? option.ar || option.en : option.en || option.ar;
  return stripHtml(raw || "");
};

const buildQuestionUpdatePayload = (
  item: QuestionDto,
  correctOption: QuestionOptionEnum,
): UpdateQuestionRequest => ({
  contents: item.contents,
  model: item.model,
  timeDuration: item.timeDuration,
  marks: item.marks,
  displayImageId: item.displayImage?.id,
  lessonId: item.lessonId,
  skills: item.skills || [],
  optionA: item.optionA,
  optionB: item.optionB,
  optionC: item.optionC,
  optionD: item.optionD,
  correctOption,
});

type QuestionCardProps = {
  item: QuestionDto;
  index: number;
  isSuperAdmin: boolean;
  isRtl: boolean;
  language: string;
  isUpdatingCorrectOption: boolean;
  onEdit: (id: string) => void;
  onDelete: (id: string) => void;
  onToggleState: (id: string) => void;
  onCorrectOptionChange: (
    item: QuestionDto,
    correctOption: QuestionOptionEnum,
  ) => void;
  t: (key: string, options?: Record<string, unknown>) => string;
};

const QuestionCard: React.FC<QuestionCardProps> = ({
  item,
  index,
  isSuperAdmin,
  isRtl,
  language,
  isUpdatingCorrectOption,
  onEdit,
  onDelete,
  onToggleState,
  onCorrectOptionChange,
  t,
}) => {
  const previewText = stripHtml(getQuestionTextHtml(item));
  const hasPreview = previewText.length > 0;

  return (
    <Card
      className={`question-card h-100${item.isActive ? "" : " question-card--inactive"}`}
      onClick={() => onEdit(item.id)}
    >
      <CardBody>
        <div className="question-card__header">
          <span className="question-card__index">{index + 1}</span>
          <div className="question-card__title-row">
            <Badge color={item.isActive ? "success" : "danger"} pill>
              {item.isActive ? t("active") : t("inActive")}
            </Badge>
            {questionHasImage(item) && (
              <Badge color="info" pill>
                <i className="ri-image-line me-1 align-bottom" />
                {t("image", { defaultValue: "Image" })}
              </Badge>
            )}
          </div>
          <div
            className="question-card__menu"
            onClick={(e) => e.stopPropagation()}
          >
            <UncontrolledDropdown direction={isRtl ? "start" : "end"}>
              <DropdownToggle
                tag="a"
                className="dropdown-toggle"
                role="button"
                aria-label={t("actions", { defaultValue: "Actions" })}
              ></DropdownToggle>
              <DropdownMenu end={!isRtl}>
                <DropdownItem onClick={() => onEdit(item.id)}>
                  <i className="ri-pencil-line me-2 align-bottom text-muted" />
                  {t("edit")}
                </DropdownItem>
                {isSuperAdmin && (
                  <DropdownItem onClick={() => onToggleState(item.id)}>
                    <i
                      className={`${
                        item.isActive
                          ? "ri-forbid-line"
                          : "ri-checkbox-circle-line text-success"
                      } me-2 align-bottom text-muted`}
                    />
                    {item.isActive ? t("deactivate") : t("activate")}
                  </DropdownItem>
                )}
                <DropdownItem
                  className="text-danger"
                  onClick={() => onDelete(item.id)}
                >
                  <i className="ri-delete-bin-5-line me-2 align-bottom" />
                  {t("delete")}
                </DropdownItem>
              </DropdownMenu>
            </UncontrolledDropdown>
          </div>
        </div>

        <div
          className={`question-card__preview${hasPreview ? "" : " question-card__preview--empty"}`}
        >
          {hasPreview
            ? previewText
            : t("noQuestionText", { defaultValue: "No question text" })}
        </div>

        <div
          className="question-card__answers"
          onClick={(e) => e.stopPropagation()}
        >
          <p className="question-card__answers-heading mb-0">
            <i className="ri-list-check-2" />
            {t("options", { defaultValue: "Options" })}
          </p>

          <div className="question-card__correct-row">
            <Label className="question-card__correct-label mb-0">
              <i className="ri-checkbox-circle-fill" />
              {t("correctAnswer")}
            </Label>
            <div className="d-flex align-items-center gap-2">
              {isUpdatingCorrectOption && <Spinner size="sm" color="primary" />}
              <div className="question-card__correct-select-wrap">
                <Input
                  type="select"
                  bsSize="sm"
                  className="question-card__correct-select"
                  value={item.correctOption}
                  disabled={isUpdatingCorrectOption}
                  aria-label={t("chooseCorrectAnswer")}
                  onChange={(e) =>
                    onCorrectOptionChange(
                      item,
                      e.target.value as QuestionOptionEnum,
                    )
                  }
                >
                  {QUESTION_OPTIONS.map((option) => (
                    <option key={option} value={option}>
                      {t(OPTION_LETTER_MAP[option])}
                    </option>
                  ))}
                </Input>
              </div>
            </div>
          </div>

          <ul className="question-card__options-list">
            {QUESTION_OPTIONS.map((option) => {
              const optionText = getOptionDisplayText(
                getOptionLocalized(item, option),
                language,
              );
              const isCorrect = item.correctOption === option;
              return (
                <li
                  key={option}
                  className={`question-card__option-item${isCorrect ? " question-card__option-item--correct" : ""}`}
                >
                  <span className="question-card__option-letter">
                    {OPTION_LETTER_MAP[option]}
                  </span>
                  <span className="question-card__option-text">
                    {optionText || (
                      <span className="question-card__option-empty">—</span>
                    )}
                  </span>
                  {isCorrect && (
                    <i className="ri-check-double-line question-card__option-check" />
                  )}
                </li>
              );
            })}
          </ul>
        </div>

        <div className="question-card__footer">
          <span className="question-card__meta question-card__meta--marks">
            <i className="ri-star-fill" />
            {t("marks")}: <strong>{item.marks}</strong>
          </span>
          <span className="question-card__meta question-card__meta--time">
            <i className="ri-time-fill" />
            {item.timeDuration}
          </span>
        </div>
      </CardBody>
    </Card>
  );
};

export const QuestionsPage: React.FC<TProps> = ({
  lessonId,
  model,
  subjectId,
}) => {
  //#region hooks
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language === "ar";
  const language = i18n.language;
  //#endregion

  //#region internal state
  const [showQuestionDeleteModal, setShowQuestionDeleteModal] =
    useState<boolean>(false);
  const [deletingQuestionId, setDeletingQuestionId] = useState<string | null>(
    null,
  );
  const [showAddEditQuestionModal, setShowAddEditQuestionModal] =
    useState<boolean>(false);
  const [editingQuestionId, setEditingQuestionId] = useState<string | null>(
    null,
  );
  const [updatingCorrectOptionId, setUpdatingCorrectOptionId] = useState<
    string | null
  >(null);
  //#endregion

  //#region api
  const { data: questions, isLoading: isLoadingQuestions } =
    useGetQuestionsByLessonQuery({ lessonId, model }, { skip: !lessonId });

  const [deleteQuestion, { isLoading: isLoadingDeleteQuestion }] =
    useDeleteQuestionMutation();

  const [toggleQuestionState] = useToggleQuestionStateMutation();

  const [updateQuestionCorrectOption] =
    useUpdateQuestionCorrectOptionMutation();

  const profile = useSelector(
    (state: { account: AccountState }) => state.account.userProfile,
  );

  const isSuperAdmin = profile?.role.key === RoleEnum.SUPER_ADMIN;
  //#endregion

  //#region handlers

  const onAdd = () => {
    setEditingQuestionId(null);
    setShowAddEditQuestionModal(true);
  };

  const onEdit = (id: string) => {
    setEditingQuestionId(id);
    setShowAddEditQuestionModal(true);
  };

  const onDelete = (id: string) => {
    setDeletingQuestionId(id);
    setShowQuestionDeleteModal(true);
  };

  const onDeleteConfirm = async () => {
    try {
      await deleteQuestion(deletingQuestionId as string).unwrap();
      setDeletingQuestionId(null);
      setShowQuestionDeleteModal(false);
    } catch (err) {}
  };

  const onToggleState = async (id: string) => {
    try {
      await toggleQuestionState(id).unwrap();
    } catch (err) {}
  };

  const onCorrectOptionChange = async (
    item: QuestionDto,
    correctOption: QuestionOptionEnum,
  ) => {
    if (correctOption === item.correctOption) return;

    setUpdatingCorrectOptionId(item.id);
    try {
      await updateQuestionCorrectOption({
        id: item.id,
        correctOption: correctOption,
      }).unwrap();
      toast.success(t("savedSuccessfully"), { autoClose: 3000 });
    } catch (err) {
      const errMsg =
        (err as { data?: { message?: string } })?.data?.message || t("error");
      toast.error(errMsg, { autoClose: 3000 });
    } finally {
      setUpdatingCorrectOptionId(null);
    }
  };

  //#endregion

  return (
    <React.Fragment>
      <Row className="mb-4 align-items-center">
        <Col className="col-sm">
          <div className="d-flex justify-content-sm-end gap-2">
            <Button color="success" onClick={onAdd}>
              <i className="ri-add-fill me-1 align-bottom"></i>
              {t("addItem", { item: "question" })}
            </Button>
          </div>
        </Col>
      </Row>

      <Row className="mt-3 question-cards-grid g-3">
        {isLoadingQuestions && <TableSpinner />}
        {!isLoadingQuestions && (!questions || questions.length === 0) && (
          <NoResultFound />
        )}
        {!isLoadingQuestions &&
          questions &&
          questions.length > 0 &&
          questions.map((item: QuestionDto, index: number) => (
            <Col key={item.id} xs={12} lg={6}>
              <QuestionCard
                item={item}
                index={index}
                isSuperAdmin={isSuperAdmin}
                isRtl={isRtl}
                language={language}
                isUpdatingCorrectOption={updatingCorrectOptionId === item.id}
                onEdit={onEdit}
                onDelete={onDelete}
                onToggleState={onToggleState}
                onCorrectOptionChange={onCorrectOptionChange}
                t={t}
              />
            </Col>
          ))}
      </Row>
      <DeleteModal
        loading={isLoadingDeleteQuestion}
        show={showQuestionDeleteModal}
        onDeleteClick={() => onDeleteConfirm()}
        onCloseClick={() => {
          setShowQuestionDeleteModal(false);
        }}
      />
      <AddEditQuestionsModal
        lessonId={lessonId}
        subjectId={subjectId}
        id={editingQuestionId}
        show={showAddEditQuestionModal}
        onCloseClick={() => {
          setShowAddEditQuestionModal(false);
          setEditingQuestionId(null);
        }}
        model={model}
      />
    </React.Fragment>
  );
};
